// FW10/FW12 — frontend mirror of the consumer's
// `lib/workflow/util/whitelist.ts` (`STANDARD_FIELDS` ∪ `SERVICE_ROUTED_FIELDS`).
// Keep in sync with main-consumer to ensure the UPDATE_GUEST field picker only
// lists fields the executor will accept on the canonical column (anything else
// falls through to additionalFields.* or — for privileged columns like
// `acceptanceState` / `code` / `username` — is rejected outright).
//
// `state` (FW12) is writable but is NOT a direct Mongo write: the consumer
// routes it to main-backend's `/api/v1/internal/guest-state` RPC so the
// capacity gate, the acceptanceState sync, the countedGuests/waitlistedCount
// counters and the guest_logs row all still run. A full event therefore makes
// the action FAIL (visible on the workflow log) rather than overselling — worth
// knowing when writing the picker's help text.
export const GUEST_UPDATE_FIELD_WHITELIST: ReadonlySet<string> = new Set([
  "firstName",
  "lastName",
  "email",
  "phone",
  "mobile",
  "company",
  "jobTitle",
  "title",
  "gender",
  "address",
  "address2",
  "zipCode",
  "city",
  "country",
  "language",
  "state",
]);

/**
 * Closed value sets for the enum-typed writable columns — the frontend mirror
 * of the consumer's `ENUM_FIELD_VALUES` (`lib/workflow/util/whitelist.ts`).
 *
 * The executor rejects anything outside these, and its standard-field lane
 * does NO coercion beyond upper-casing, so a value that slips through here
 * produces a pipe that saves green and then fails on every run. Mirror ONLY
 * what the executor actually validates: `language` is option-typed in the
 * field catalog too, but the executor writes it verbatim, so gating it here
 * would block configs the runtime accepts.
 */
export const GUEST_UPDATE_ENUM_VALUES: ReadonlyMap<string, readonly string[]> =
  new Map([
    ["state", ["UNCONTACTED", "CONTACTED", "ACCEPTED", "DECLINED", "WAITLISTED"]],
    ["gender", ["M", "F", "D"]],
  ]);

/**
 * Writable columns the consumer does NOT `$set` directly — it hands them to
 * main-backend's RPC so the lifecycle machinery runs (mirror of the consumer's
 * `SERVICE_ROUTED_FIELDS`, `lib/workflow/util/whitelist.ts`).
 *
 * Consequences the UI must respect, both from
 * `mongo-guest-update-gateway.ts:116-132`:
 *  - the write can be REFUSED at runtime (a full event 409s on the capacity
 *    CAS), so its outcome is never knowable client-side — never render a
 *    predicted value for one of these;
 *  - increment/decrement is rejected outright (non-numeric column);
 *  - an empty value is rejected outright (there is no "no status").
 */
export const SERVICE_ROUTED_FIELDS: ReadonlySet<string> = new Set(["state"]);

/**
 * Writable columns that cannot be cleared. Every other whitelisted field
 * treats an empty value as a legitimate `$unset`; `state` has no "no status"
 * to clear to, so the executor rejects an empty value outright.
 *
 * DERIVED from {@link SERVICE_ROUTED_FIELDS} rather than restated: the gateway's
 * empty-value refusal lives inside its service-routed branch, so the two sets
 * are the same fact. Writing them out twice is how they drift.
 */
export const GUEST_UPDATE_VALUE_REQUIRED: ReadonlySet<string> =
  SERVICE_ROUTED_FIELDS;

/**
 * May a predicted value for this field be rendered into a row before the server
 * confirms it?
 *
 * Only plain `set` writes to inert columns qualify. Never true for:
 *  - {@link SERVICE_ROUTED_FIELDS} — the server can refuse them, and a
 *    successful write also mutates `acceptanceState`, `wasWaitlisted`, the event
 *    counters and the company contingent, none of which we would paint;
 *  - `acceptanceState` and the API bookkeeping keys — already stripped by
 *    `mergeGuestPatch` (`guest-patch.ts:22-30`);
 *  - increment/decrement targets — `$inc` applies against the STORED value under
 *    an idempotency claim that skips on redelivery, so `current + delta` on the
 *    client can be off by a whole delta.
 *
 * Custom fields (`additionalFields.*`) are paintable when set, since they are
 * plain `$set`s with no lifecycle behind them.
 */
const NEVER_PAINTABLE: ReadonlySet<string> = new Set([
  "acceptanceState",
  "guestId",
  "forceWaitlist",
  "overrideRules",
  "clearFields",
]);

export function isPaintableField(
  field: string,
  op: "set" | "delta" = "set",
): boolean {
  if (op !== "set") return false;
  if (SERVICE_ROUTED_FIELDS.has(field)) return false;
  if (NEVER_PAINTABLE.has(field)) return false;
  if (field.startsWith("additionalFields.")) return true;
  return GUEST_UPDATE_FIELD_WHITELIST.has(field);
}
